import Image from 'next/image';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/DesignSystem/Component';
import { useRubikaUser, useShadUser } from '@/Module/Auth/Hook';
import { FILE_BROWSER_URL } from '@/Module/Core/Constant';
import { useDeviceType } from '@/Module/Core/Hook';
import { DeviceType } from '@/Module/Core/Model/Enum';
import { useCampaign } from '@/Module/DefaultLayout/Hook';
import { IconClose2 } from '@/Module/Icon';
import { useAppSelector } from '@/Module/Store/Hook';
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}
interface CustomSnackbarContentProps {
  handleInstallClick: () => void;
  handleCloseClick: () => void;
}
const STORAGE_KEY = 'pwa_install_prompt_dismissed';
const CustomSnackbarContent: FC<CustomSnackbarContentProps> = ({
  handleInstallClick,
  handleCloseClick
}) => <div className="flex flex-row gap-3" data-sentry-component="CustomSnackbarContent" data-sentry-source-file="InstallPWA.tsx">
    <Button variant="text" color="primary" bold onClick={handleCloseClick} data-sentry-element="Button" data-sentry-source-file="InstallPWA.tsx">
      <IconClose2 className="text-black " size="small" data-sentry-element="IconClose2" data-sentry-source-file="InstallPWA.tsx" />
    </Button>
    <div className="flex w-[72vw] flex-row gap-4 pt-1">
      <Image className="h-[40px] w-[40px]" src={`${FILE_BROWSER_URL}/img/ino.webp`} alt="App Icon" width="40" height="40" data-sentry-element="Image" data-sentry-source-file="InstallPWA.tsx" />
      <div className="flex h-[40px] grow justify-between">
        <div className="flex flex-col items-start justify-between">
          <span style={{
          fontWeight: 'bold',
          display: 'block'
        }}>نصب وب اپلیکیشن آی‌نو</span>
          <span className="pt-1 text-gray-500">Ino.School</span>
        </div>
        <Button className="pb-1" variant="text" color="primary" bold onClick={handleInstallClick} data-sentry-element="Button" data-sentry-source-file="InstallPWA.tsx">
          نصب
        </Button>
      </div>
    </div>
  </div>;
const InstallPWA = () => {
  const deviceType = useDeviceType();
  const {
    bannerEnabled
  } = useCampaign();
  const isLoggedIn = useAppSelector(state => state.auth.isLoggedIn);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const isRubikaUser = useRubikaUser();
  const isShadUser = useShadUser();
  const handleInstallClick = useCallback(() => {
    if (!deferredPrompt) {
      return;
    }
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then(() => {
      setDeferredPrompt(null);
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      closeSnackbar();
    });
  }, [deferredPrompt]);
  const handleCloseClick = useCallback(() => {
    setDeferredPrompt(null);
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    closeSnackbar();
  }, [deferredPrompt]);
  const showInstallPrompt = useCallback(() => {
    enqueueSnackbar(<CustomSnackbarContent handleInstallClick={handleInstallClick} handleCloseClick={handleCloseClick} />, {
      variant: 'default',
      persist: true,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      },
      style: {
        backgroundColor: 'white',
        color: 'black',
        borderRadius: '8px',
        padding: '4px 16px',
        position: 'fixed',
        top: bannerEnabled ? '70px' : '60px',
        left: '1%',
        right: '1%',
        zIndex: 9999
      }
    });
  }, [handleInstallClick]);
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    };
  }, []);
  useEffect(() => {
    if (!isLoggedIn || deviceType !== DeviceType.MOBILE || process.env.NEXT_PUBLIC_BUILD_ENV !== 'PRODUCTION' || !deferredPrompt || isRubikaUser || isShadUser) return;
    const lastDismissed = localStorage.getItem(STORAGE_KEY);
    if (lastDismissed) {
      const lastDismissedDate = new Date(lastDismissed);
      const currentDate = new Date();
      const differenceInDays = (currentDate.getTime() - lastDismissedDate.getTime()) / (1000 * 3600 * 24);
      if (differenceInDays < 3) {
        return;
      }
    }
    showInstallPrompt();
  }, [deferredPrompt, showInstallPrompt]);
  return null;
};
export default InstallPWA;